// #region Imports
import ContentPasteGoIcon from "@mui/icons-material/ContentPasteGo";
import DownloadIcon from "@mui/icons-material/Download";
import UploadIcon from "@mui/icons-material/Upload";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material";
import { styled } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import type { MRT_PaginationState as PaginationState, MRT_SortingState as SortingState } from "material-react-table";
import { enqueueSnackbar } from "notistack";
import React, { useCallback, useMemo } from "react";
import { defaultStyles, FileIcon } from "react-file-icon";
import { useSelector } from "react-redux";
import {
  getApiErrorData,
  getApiErrorMessage,
  getApiErrorStatus,
  getErrorMessageFromUnknown,
} from "_shared/_errors/api-error-message";
import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import downloadFile from "_shared/_utilities/download-file";
import { enumKeyOf } from "_shared/_utilities/enum-utils";
import Guid from "_shared/guids/guid";
import LoadingButton from "_shared/_views/loading/loading-button";
import RowActions from "_shared/_views/tables/row-actions";
import TemplateSelector, {
  type TemplateSelectorRow,
  type TemplateSelectorSelectionChange,
} from "_shared/_views/templates/template-selector";
import { type TemplateOwnershipScope } from "_shared/_views/templates/template-ownership-filter";
import { createBulkDocumentRequests } from "documents/_api/commands/documents-form-api-request";
import {
  useCreateDocumentsMutation,
  useGetDocumentsQuery,
  useLazyDownloadDocumentQuery,
} from "documents/_api/document-api";
import { getDocumentUploadSnackbar, summarizeDocumentUploadResults } from "documents/_api/document-upload-results";
import DocumentQuery, {
  DocumentFilterParameters,
  DocumentOrderField,
  DocumentOrderParameters,
} from "documents/_api/queries/document-query";
import DocumentProcessingState from "documents/classification/_views/document-processing-state";
import Document from "documents/document";
import { DocumentFileType } from "documents/document-file-type";
import DocumentTopic from "documents/topics/document-topic";
import { getSessionPermissions } from "permissions/_stores/permissions-redux-slice";
import { useSession } from "users/__session/session-context";
// #endregion

// #region Styles
const FileName = styled(Typography)(() => ({
  flex: "1 1 auto",
  fontSize: "1em",
  fontWeight: "500",
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));
const FileIconContainer = styled("span")(() => ({
  display: "block",
  flex: "0 0 24px",
  height: "30px",
  width: "24px",
  svg: {
    display: "block",
    height: "100%",
    width: "100%",
  },
}));
const NameTextContainer = styled("div")(() => ({
  display: "flex",
  flex: "1 1 auto",
  flexDirection: "column",
  minWidth: 0,
}));
const OwnershipText = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: "0.75rem",
  lineHeight: 1.2,
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));
const FileNameRow = styled("div")(() => ({
  display: "flex",
  alignItems: "center",
  minWidth: 0,
}));
const UploadTemplateButton = styled(LoadingButton)(({ theme }) => ({
  whiteSpace: "normal",
  wordBreak: "keep-all",
  "& .MuiButton-startIcon": {
    flexShrink: 0,
  },
  [theme.breakpoints.down("md")]: {
    lineHeight: 1.1,
    maxWidth: theme.spacing(12),
  },
}));
const UploadTemplateButtonLabel = styled("span")(() => ({
  overflowWrap: "normal",
  whiteSpace: "normal",
  wordBreak: "keep-all",
}));
// #endregion

type DocumentSelectorProps = {
  isLoading?: boolean;
  /**
   * Whether the surface drawing this is too narrow to hold the selector open. Defaults to the
   * viewport's `md` breakpoint, which is the right question wherever the selector has the window to
   * itself; a proposal tab passes what its pane measured instead.
   */
  compact?: boolean;
  documentType: "conflicts" | "policy";
  proposalId?: Guid;
  selectedTab?: "templates" | "documents";
  selectedDocumentIds: Guid[];
  selectedTemplateIds?: Guid[];
  onDocumentSelectionChanged: (selectedDocs: Document[], deselectedDocs: Document[]) => void;
  onCreateDocumentFromTemplate: (templateId: Guid) => void;
};

const DEFAULT_OWNERSHIP_SCOPE: TemplateOwnershipScope = "all";

/**
 * What the panel calls a document's row.
 *
 * In one place because the panel reports a whole press back by id, so building a row and resolving
 * one have to agree on the answer — including for a document that arrived without an id, which falls
 * back to its position on the page.
 */
function documentRowId(document: Document, index: number): string {
  return document.id?.value ?? `document-${index}`;
}

/**
 * The conflicts and policies tabs' template panel: this entity's document templates and the
 * documents already on proposals, either of which can be put on the proposal being drafted.
 *
 * Everything about the panel's SHAPE — the ownership toggle, the Templates/Recent strip, the upload
 * action, the search box, the checkbox rows and the paginator — belongs to `TemplateSelector`, which
 * was lifted out of this file so the Vendor Team and Fee Schedule tabs could wear the same panel
 * (#4955). What is left here is what is true of documents and of nothing else: which query a tab and
 * an ownership scope add up to, what a document's row draws, what uploading one does, and what
 * ticking one means to the proposal.
 */
export default function DocumentSelector(props: Readonly<DocumentSelectorProps>) {
  const {
    documentType,
    proposalId,
    selectedTab,
    selectedDocumentIds,
    selectedTemplateIds,
    onDocumentSelectionChanged,
    onCreateDocumentFromTemplate,
  } = props;

  const documentFileUploaderRef = React.useRef<HTMLInputElement | null>(null);

  // The API refuses uploads without Read/Write GeneralDocumentAccess, so a user lacking it is
  // offered no upload control at all rather than a button that always fails.
  const sessionPermissions = useSelector(getSessionPermissions);
  const canUploadDocuments = Boolean(sessionPermissions?.includesReadWriteAccessToGeneralDocuments);

  const [isUploading, setIsUploading] = React.useState(false);
  const [globalFilter, setGlobalFilter] = React.useState("");
  const [pagination, setPagination] = React.useState<PaginationState>({ pageIndex: 0, pageSize: 10 });
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [activeTab, setActiveTab] = React.useState<"templates" | "documents">(selectedTab ?? "templates");
  const [ownershipScope, setOwnershipScope] = React.useState<TemplateOwnershipScope>(DEFAULT_OWNERSHIP_SCOPE);

  const theme = useTheme();
  const viewportIsMdDown = useMediaQuery(theme.breakpoints.down("md"));
  const isCompact = props.compact ?? viewportIsMdDown;
  const session = useSession();
  const currentUserId = session.user?.id;

  const documentQuery = useMemo(() => {
    const paginationParameters = PaginationParameters.create(pagination);
    const order = sorting.find((sort) => sort !== undefined);
    const orderParameters = new DocumentOrderParameters({
      orderBy: order?.id === "name" ? DocumentOrderField.Name : DocumentOrderField.Created,
      orderDir: order
        ? order.desc ? OrderDirection.Descending : OrderDirection.Ascending
        : activeTab === "documents" ? OrderDirection.Descending : OrderDirection.Ascending,
    });
    const filterParameters = new DocumentFilterParameters({
      entityClass: activeTab === "documents" ? "Proposal" : undefined,
      name: globalFilter,
      contexts: [documentType + (activeTab === "templates" ? "template" : "")],
      ownershipScope,
      creatorId: ownershipScope === "own" ? currentUserId : undefined,
    });

    return new DocumentQuery({ orderParameters, filterParameters, paginationParameters });
  }, [
    activeTab,
    currentUserId,
    documentType,
    globalFilter,
    ownershipScope,
    pagination,
    sorting,
  ]);

  const {
    data: paginatedDocuments,
    isError: errorLoading,
    isFetching,
    isLoading,
    refetch,
  } = useGetDocumentsQuery({ query: documentQuery });
  const [createDocuments] = useCreateDocumentsMutation();
  const [downloadDocument] = useLazyDownloadDocumentQuery();
  const availableDocuments = useMemo(() => paginatedDocuments?.data ?? [], [paginatedDocuments]);
  const rowCount = paginatedDocuments?.totalElements ?? 0;

  const handleDownload = useCallback(async (id: Guid): Promise<void> => {
    try {
      if (!id) {
        await Promise.reject(new Error("No document id provided"));
        return;
      }
      const response = await downloadDocument({ documentId: id.value }).unwrap();
      await downloadFile(response);
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Failed to download file", { variant: "error" });
    }
  }, [downloadDocument]);

  /**
   * Reports the page's selection after one press, however many rows that press changed.
   *
   * The caller is told about the whole visible page rather than the rows that changed, because it is
   * what it has always been told and because a page is what the panel can speak for: a document on
   * another page is neither selected nor deselected by a press here, and saying otherwise would drop
   * it.
   *
   * Every change in the press is folded in before the caller hears anything. One press reported once
   * is the whole point: the tabs above this accumulate into a `proposalBuilder` they captured at
   * render, so a select-all reported row by row had each call overwrite the last and committed a
   * single document of the three on the page (#5163).
   */
  const reportSelection = useCallback(
    (changes: readonly { document: Document; selected: boolean }[]) => {
      const selectedIds = new Map(selectedDocumentIds.map((id) => [id.value, id]));
      for (const { document, selected } of changes) {
        if (!document.id?.value) continue;
        if (selected) selectedIds.set(document.id.value, document.id);
        else selectedIds.delete(document.id.value);
      }
      const isSelected = (candidate: Document) => Boolean(candidate.id?.value && selectedIds.has(candidate.id.value));
      onDocumentSelectionChanged(
        availableDocuments.filter(isSelected),
        availableDocuments.filter((candidate) => !isSelected(candidate)),
      );
    },
    [availableDocuments, onDocumentSelectionChanged, selectedDocumentIds],
  );

  /**
   * The same report, from the panel's whole-press callback, where the rows are named by id.
   *
   * A row the page no longer holds is dropped rather than guessed at — the press is reported against
   * the page that was on screen when it happened.
   */
  const reportSelectionChanges = useCallback(
    (changes: readonly TemplateSelectorSelectionChange[]) => {
      const documentsByRowId = new Map(
        availableDocuments.map((document, index) => [documentRowId(document, index), document]),
      );
      reportSelection(
        changes
          .map((change) => {
            const document = documentsByRowId.get(change.id);
            return document ? { document, selected: change.selected } : undefined;
          })
          .filter((change): change is { document: Document; selected: boolean } => change !== undefined),
      );
    },
    [availableDocuments, reportSelection],
  );

  const rows: TemplateSelectorRow[] = useMemo(
    () =>
      availableDocuments.map((document, index) => {
        const documentName = document.name?.value ?? "document";
        const cannotCreateFromTemplate = !proposalId || proposalId.isEmpty;
        return {
          id: documentRowId(document, index),
          name: documentName,
          content: <DocumentNameCell currentUserId={currentUserId} document={document} />,
          selected: selectedDocumentIds.some((id) => document.id.isEqualTo(id)),
          indeterminate: selectedTemplateIds?.some((id) => document.id.isEqualTo(id)) ?? false,
          actions: (
            // The selector's surface is not always the window — on a proposal tab it is the page's
            // pane — so the collapse is driven by the same measurement the rest of this uses.
            <RowActions
              collapsed={isCompact}
              ariaLabel={`Actions for ${documentName}`}
              actions={[
                {
                  key: "create-from-template",
                  label: "Create Document from Template",
                  ariaLabel: `Create document from ${documentName}`,
                  icon: <ContentPasteGoIcon />,
                  disabled: cannotCreateFromTemplate,
                  disabledReason: "Save the proposal before creating a document from a template",
                  onClick: () => onCreateDocumentFromTemplate(document.id),
                },
                {
                  key: "download",
                  label: "Download",
                  ariaLabel: `Download ${documentName}`,
                  icon: <DownloadIcon />,
                  onClick: () => {
                    void handleDownload(document.id);
                  },
                },
              ]}
            />
          ),
          onSelectedChanged: (selected: boolean) => reportSelection([{ document, selected }]),
        };
      }),
    [
      availableDocuments,
      currentUserId,
      handleDownload,
      isCompact,
      onCreateDocumentFromTemplate,
      proposalId,
      reportSelection,
      selectedDocumentIds,
      selectedTemplateIds,
    ],
  );

  const uploadDisabled = activeTab !== "templates" && (!proposalId || proposalId.isEmpty);
  const uploadTooltip = uploadDisabled ? "Save proposal before uploading documents" : "Upload Template";

  // Declared above its use rather than hoisted below the return: a component that calls a `function`
  // declared further down is one the React Compiler gives up on entirely (app/CLAUDE.md).
  const handleDocumentFileUpload = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    if (!event.currentTarget.files) return;

    if (activeTab !== "templates" && (!proposalId || proposalId.isEmpty)) {
      enqueueSnackbar("Proposal must be saved before uploading documents.", { variant: "error" });
      event.target.value = "";
      return;
    }

    const files = Array.from(event.currentTarget.files);
    if (files.length === 0) return;

    setIsUploading(true);
    const input = event.target;
    await uploadDocumentFiles({ activeTab, createDocuments, documentType, files, proposalId });
    input.value = "";
    setIsUploading(false);
  };

  return (
    <TemplateSelector
      action={
        canUploadDocuments ? (
          <Tooltip title={uploadTooltip}>
            <span>
              <UploadTemplateButton
                color="primary"
                disabled={uploadDisabled}
                loading={isUploading}
                startIcon={<UploadIcon />}
                onClick={() => documentFileUploaderRef.current?.click()}
              >
                <UploadTemplateButtonLabel>Upload Template</UploadTemplateButtonLabel>
              </UploadTemplateButton>
            </span>
          </Tooltip>
        ) : undefined
      }
      activeTab={activeTab}
      collapsedLabel="Document Templates"
      compact={isCompact}
      ownership={{
        // The group keeps the name the browser suite has always reached the conflicts and policies
        // panels by; the options' names fall out of "documents" unchanged.
        groupLabel: "Document ownership filter",
        label: "documents",
        value: ownershipScope,
        onChange: setOwnershipScope,
      }}
      pagination={{ onChange: setPagination, rowCount, value: pagination }}
      rows={rows}
      search={{ onChange: setGlobalFilter, value: globalFilter }}
      selectedCount={selectedDocumentIds.length}
      sorting={{ onChange: setSorting, value: sorting }}
      status={{ isError: errorLoading, isFetching, isLoading, onRetry: () => void refetch() }}
      subject="templates"
      tabs={DOCUMENT_SELECTOR_TABS}
      onSelectionChanged={reportSelectionChanges}
      onTabChanged={(value) => setActiveTab(value as "templates" | "documents")}
    >
      {canUploadDocuments && (
        <input
          type="file"
          hidden={true}
          multiple={true}
          ref={documentFileUploaderRef}
          onChange={(event) => void handleDocumentFileUpload(event)}
        />
      )}
    </TemplateSelector>
  );
}

/** The two lists the panel offers: this entity's templates, and the documents already on proposals. */
const DOCUMENT_SELECTOR_TABS = [
  { label: "Templates", value: "templates" },
  { label: "Recent", value: "documents" },
] as const;

/**
 * Sends the chosen files and says how it went.
 *
 * At module scope on purpose: it is one `try`/`catch` around an `await`, and the React Compiler bails
 * a whole component out over control flow like this — see app/CLAUDE.md. Nothing here reads the
 * render, so passing what it needs costs nothing.
 */
async function uploadDocumentFiles(params: {
  activeTab: "templates" | "documents";
  createDocuments: ReturnType<typeof useCreateDocumentsMutation>[0];
  documentType: "conflicts" | "policy";
  files: File[];
  proposalId?: Guid;
}): Promise<void> {
  const { activeTab, createDocuments, documentType, files, proposalId } = params;
  try {
    let context = documentType === "conflicts" ? "Conflicts" : "Policy";
    if (activeTab === "templates") {
      context = context.concat("Template");
    }
    const topics = [DocumentTopic.create({ context })];
    if (proposalId && !proposalId.isEmpty) {
      topics.push(DocumentTopic.create({ entityClass: "Proposal", entityId: proposalId }));
    }
    const requests = createBulkDocumentRequests(files, { topics });
    const results = await createDocuments({ requests }).unwrap();
    const summary = summarizeDocumentUploadResults(results, requests);
    const snackbar = getDocumentUploadSnackbar(summary);
    enqueueSnackbar(snackbar.message, { variant: snackbar.variant });
  } catch (error) {
    const status = getApiErrorStatus(error);
    if (status === 415 || status === 422) {
      enqueueSnackbar("Unsupported file type", { variant: "error" });
    } else {
      enqueueSnackbar(
        getApiErrorMessage(getApiErrorData(error))
          ?? getErrorMessageFromUnknown(error)
          ?? "Unknown error occurred during upload",
        { variant: "error" },
      );
    }
  }
}

function DocumentNameCell(
  props: Readonly<{
    document: Document;
    currentUserId?: Guid;
  }>,
) {
  const { currentUserId, document } = props;
  const fileType = enumKeyOf(DocumentFileType, document.fileType?.toString()).toLocaleLowerCase();
  const ownershipLabel = getOwnershipLabel(document, currentUserId);
  return (
    <>
      <FileIconContainer>
        <FileIcon extension={fileType} {...defaultStyles[fileType as keyof typeof defaultStyles]} />
      </FileIconContainer>
      <NameTextContainer>
        <FileNameRow>
          <FileName>{document.name?.value}</FileName>
          {/*
            Shown but still selectable. Hiding a document that is being processed would trade
            this confusion for a worse one - a file the user just uploaded, and can see in their
            document list, missing from the picker with no explanation. Saying why it is not
            ready yet leaves the choice with them.
          */}
          <DocumentProcessingState status={document.classificationStatus} />
        </FileNameRow>
        <OwnershipText>{ownershipLabel}</OwnershipText>
      </NameTextContainer>
    </>
  );
}

function getOwnershipLabel(document: Document, currentUserId: Guid | undefined): string {
  if (document.creatorId && currentUserId && document.creatorId.isEqualTo(currentUserId)) {
    return "Owned by you";
  }
  return document.creatorId ? "Accessible document" : "Ownership unknown";
}
